import classNames from 'classnames';
import * as React from 'react';
import type { ReactNode } from 'react';
import type { TabKey } from '../TabList/Tab';
import { Tab } from '../TabList/Tab';
import { TabList } from '../TabList/TabList';
import { TabPanel } from './TabPanel';
import styles from './Tabs.module.css';

export interface TabsItemProps {
    tabKey: TabKey;
    tabName: string;
    tabContent: ReactNode;
    tabClassName?: string;
    tabSelectedClassName?: string;
}

export interface TabsProps {
    name: string;
    selectedTab: TabKey;
    tabs: TabsItemProps[];
    children?: ReactNode;
    onSelect?(tabKey: TabKey): void;
    className?: string;
    tabsListClassName?: string;
    panelsClassName?: string;
    panelClassName?: string;
    TabListWrapper?: React.FC<React.PropsWithChildren>;
    beforeTabs?: ReactNode;
    afterTabs?: ReactNode;
    tabListRef?: React.RefObject<HTMLDivElement>;
}

export const Tabs: React.FC<React.PropsWithChildren<TabsProps>> = props => {
    const {
        className,
        tabsListClassName,
        panelsClassName,
        panelClassName,
        tabs,
        selectedTab,
        name,
        onSelect,
        TabListWrapper = React.Fragment,
        tabListRef,
    } = props;

    const [currentTab, setCurrentTab] = React.useState(selectedTab || tabs[0].tabKey);

    React.useEffect(() => setCurrentTab(selectedTab), [selectedTab]);

    const handleSelect = (tabKey: TabKey) => {
        setCurrentTab(tabKey);
        onSelect && onSelect(tabKey);
    };

    const isTabSelected = (tabKey: TabKey) => tabKey === currentTab;

    const renderTab = (tabProps: TabsItemProps) => {
        const { tabName, tabClassName, tabSelectedClassName, tabKey } = tabProps;
        const isSelected = isTabSelected(tabKey);

        return (
            <Tab
                key={tabKey}
                tabKey={tabKey}
                className={classNames(tabClassName, isSelected && tabSelectedClassName)}
                selected={isSelected}
                onSelect={handleSelect}
            >
                {tabName}
            </Tab>
        );
    };

    const renderPanel = (tabProps: TabsItemProps) => {
        const { tabContent, tabKey } = tabProps;

        return (
            <TabPanel
                key={tabKey}
                tabKey={tabKey}
                className={classNames(panelClassName, !isTabSelected(tabKey) && styles['Panel--inactive'])}
            >
                {tabContent}
            </TabPanel>
        );
    };

    const renderTabList = () => (
        <TabList name={name} className={tabsListClassName} ref={tabListRef}>
            {tabs.map(renderTab)}
        </TabList>
    );

    return (
        <div className={classNames(className)}>
            <TabListWrapper>{renderTabList()}</TabListWrapper>
            <div className={classNames(styles.Panels, panelsClassName)}>{tabs.map(renderPanel)}</div>
        </div>
    );
};
